import React, { useState, useEffect, useMemo } from 'react';
import { useLocalization } from '../../hooks/useLocalization';
import EmailSettingsManager from './EmailSettingsManager';

interface PaymentLogEntry {
    timestamp: string;
    action: string;
    status: string;
    details?: Record<string, unknown>;
}

interface FlightSegment {
    type?: string;
    direction?: string;
    departureAirport?: string;
    arrivalAirport?: string;
    departureDate?: string;
    arrivalDate?: string;
    carrier?: string;
    flightNumber?: string;
    cabinClass?: string;
    serviceName?: string;
}

interface Booking {
    id: string;
    reservationNumber?: string;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    serviceType: 'Hotel' | 'Flight' | 'Tour' | 'Transfer';
    serviceName: string;
    destination: string;
    checkIn: string;
    checkOut: string;
    guests: number;
    rooms: number;
    amount: number;
    currency: string;
    status: 'Confirmed' | 'Pending' | 'Cancelled' | 'Completed';
    paymentStatus: 'Paid' | 'Unpaid' | 'Partial' | 'Refunded';
    createdAt: string;
    notes: string;
    paymentLogs?: PaymentLogEntry[];
    transactionId?: string;
    paymentTransactionId?: string;
    flights?: FlightSegment[];
    boardType?: string;
    roomType?: string;
    nights?: number;
}

const emptyBooking: Omit<Booking, 'id' | 'createdAt'> = {
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    serviceType: 'Hotel',
    serviceName: '',
    destination: '',
    checkIn: '',
    checkOut: '',
    guests: 2,
    rooms: 1,
    amount: 0,
    currency: 'EUR',
    status: 'Pending',
    paymentStatus: 'Unpaid',
    notes: ''
};

/** Map a MongoDB Booking document to the component's Booking interface */
const mapMongoBooking = (doc: any): Booking => {
    const passengers = doc.passengers || [];
    const lead = passengers[0] || {};
    const customerName = lead.firstName && lead.lastName
        ? `${lead.firstName} ${lead.lastName}`
        : doc.contact?.email || 'Unknown';
    const city = doc.hotel?.location?.city || '';
    const country = doc.hotel?.location?.country || '';
    const destination = [city, country].filter(Boolean).join(', ');

    // Map payment status
    let paymentStatus: 'Paid' | 'Unpaid' | 'Partial' | 'Refunded' = 'Unpaid';
    if (doc.payment?.paymentStatus === 'Completed') paymentStatus = 'Paid';
    else if (doc.payment?.paymentStatus === 'Refunded') paymentStatus = 'Refunded';
    else if (doc.payment?.paymentStatus === 'Pending') paymentStatus = 'Unpaid';

    const flights: FlightSegment[] = (doc.flights || []).map((f: any) => ({
        type: f.type || 'Flight',
        direction: f.direction || 'Outbound',
        departureAirport: f.departureAirport || '',
        arrivalAirport: f.arrivalAirport || '',
        departureDate: f.departureDate || '',
        arrivalDate: f.arrivalDate || '',
        carrier: f.carrier || '',
        flightNumber: f.flightNumber || '',
        cabinClass: f.cabinClass || '',
        serviceName: f.serviceName || ''
    }));
    const hasFlights = flights.length > 0;
    const serviceType: 'Hotel' | 'Flight' | 'Tour' | 'Transfer' = hasFlights ? 'Tour' : 'Hotel';

    return {
        id: doc._id || doc.reservationNumber || 'N/A',
        reservationNumber: doc.reservationNumber,
        customerName,
        customerEmail: doc.contact?.email || '',
        customerPhone: doc.contact?.phone || '',
        serviceType,
        serviceName: doc.hotel?.name || 'Hotel',
        destination,
        checkIn: doc.offer?.checkIn ? doc.offer.checkIn.split('T')[0] : '',
        checkOut: doc.offer?.checkOut ? doc.offer.checkOut.split('T')[0] : '',
        guests: passengers.length || 1,
        rooms: 1,
        amount: doc.payment?.totalPrice || 0,
        currency: doc.payment?.currency || 'EUR',
        status: doc.status || 'Pending',
        paymentStatus,
        createdAt: doc.createdAt ? new Date(doc.createdAt).toISOString().split('T')[0] : '',
        notes: doc.notes || '',
        paymentLogs: doc.paymentLogs || [],
        transactionId: doc.tourvisioTransactionId || '',
        paymentTransactionId: doc.payment?.paymentTransactionId || '',
        flights,
        boardType: doc.offer?.board || '',
        roomType: doc.offer?.roomType || '',
        nights: doc.offer?.nights || 0
    };
};

const BookingsManager: React.FC = () => {
    const { t, language } = useLocalization();
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<string>('All');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<Omit<Booking, 'id' | 'createdAt'>>(emptyBooking);
    const [emailSending, setEmailSending] = useState<string | null>(null);
    const [emailMessage, setEmailMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [emailLang, setEmailLang] = useState('sv');
    const [actionMenuId, setActionMenuId] = useState<string | null>(null);

    const sendBookingEmail = async (bookingId: string, type: 'confirmation' | 'reminder') => {
        setEmailSending(type);
        setEmailMessage(null);
        try {
            const url = type === 'confirmation' ? `/api/email/send/${bookingId}` : `/api/email/send-reminder/${bookingId}`;
            const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lang: emailLang }) });
            const data = await res.json();
            if (data.success) {
                setEmailMessage({ type: 'success', text: `${type === 'confirmation' ? 'Confirmation' : 'Reminder'} email sent!` });
            } else {
                setEmailMessage({ type: 'error', text: data.message || 'Failed to send email' });
            }
        } catch (err) {
            setEmailMessage({ type: 'error', text: 'Network error' });
        } finally {
            setEmailSending(null);
        }
    };

    const resyncFlights = async (bookingId: string) => {
        setEmailSending('resync');
        setEmailMessage(null);
        try {
            const res = await fetch(`/api/bookings/${bookingId}/resync-flights`, { method: 'POST', credentials: 'include' });
            const data = await res.json();
            if (data.success) {
                setEmailMessage({ type: 'success', text: data.message || 'Flights synced!' });
                // Update the booking in our local state
                setBookings(prev => prev.map(b => {
                    if (b.id === bookingId) {
                        return { ...b, flights: data.flights, serviceType: data.flights?.length > 0 ? 'Tour' : b.serviceType };
                    }
                    return b;
                }));
                if (selectedBooking?.id === bookingId) {
                    setSelectedBooking(prev => prev ? { ...prev, flights: data.flights, serviceType: data.flights?.length > 0 ? 'Tour' : prev.serviceType } : prev);
                }
            } else {
                // Show B2C limitation as a warning, not an error
                const messageText = data.b2cLimitation
                    ? '⚠️ B2C API limitation: Flight details cannot be fetched for this booking type. Use the Edit form to add flight info manually.'
                    : (data.message || 'Failed to sync flights');
                setEmailMessage({ type: 'error', text: messageText });
            }
        } catch {
            setEmailMessage({ type: 'error', text: 'Network error' });
        } finally {
            setEmailSending(null);
        }
    };


    const itemsPerPage = 6;

    // Fetch bookings from backend API
    useEffect(() => {
        const fetchBookings = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await fetch('/api/bookings?limit=200', { credentials: 'include' });
                if (!res.ok) throw new Error(`Failed to load bookings (${res.status})`);
                const data = await res.json();
                if (data.success && Array.isArray(data.bookings)) {
                    setBookings(data.bookings.map(mapMongoBooking));
                } else {
                    setBookings([]);
                }
            } catch (err: any) {
                console.error('Failed to fetch bookings:', err);
                setError(err.message || 'Failed to load bookings');
            } finally {
                setLoading(false);
            }
        };
        fetchBookings();
    }, []);

    // Stats calculations
    const stats = useMemo(() => {
        const totalRevenue = bookings.filter(b => b.paymentStatus === 'Paid' || b.paymentStatus === 'Partial').reduce((sum, b) => sum + b.amount, 0);
        const pendingPayments = bookings.filter(b => b.paymentStatus === 'Unpaid' || b.paymentStatus === 'Partial').reduce((sum, b) => sum + b.amount, 0);
        const cancelledCount = bookings.filter(b => b.status === 'Cancelled').length;
        return {
            total: bookings.length,
            totalRevenue,
            pendingPayments,
            cancellationRate: bookings.length > 0 ? Math.round((cancelledCount / bookings.length) * 100) : 0
        };
    }, [bookings]);

    const filteredBookings = useMemo(() => {
        return bookings.filter(b => {
            const matchesSearch = b.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                b.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                b.serviceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                b.destination.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = filterStatus === 'All' || b.status === filterStatus;
            return matchesSearch && matchesStatus;
        });
    }, [bookings, searchTerm, filterStatus]);

    const paginatedBookings = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredBookings.slice(start, start + itemsPerPage);
    }, [filteredBookings, currentPage]);

    const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Confirmed': return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
            case 'Pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800';
            case 'Cancelled': return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
            case 'Completed': return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
            default: return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    const getPaymentColor = (status: string) => {
        switch (status) {
            case 'Paid': return 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400';
            case 'Partial': return 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400';
            case 'Refunded': return 'bg-gray-100 text-gray-600 line-through dark:bg-gray-700 dark:text-gray-400';
            default: return 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400';
        }
    };

    const getServiceIcon = (type: string) => {
        switch (type) {
            case 'Flight': return 'flight';
            case 'Hotel': return 'hotel';
            case 'Tour': return 'explore';
            case 'Transfer': return 'local_taxi';
            default: return 'confirmation_number';
        }
    };

    const handleOpenDetail = (booking: Booking) => {
        setSelectedBooking(booking);
        setIsDetailOpen(true);
        setActionMenuId(null);
    };

    const handleOpenCreate = () => {
        setFormData(emptyBooking);
        setIsEditing(false);
        setIsFormOpen(true);
    };

    const handleOpenEdit = (booking: Booking) => {
        setFormData({
            customerName: booking.customerName,
            customerEmail: booking.customerEmail,
            customerPhone: booking.customerPhone,
            serviceType: booking.serviceType,
            serviceName: booking.serviceName,
            destination: booking.destination,
            checkIn: booking.checkIn,
            checkOut: booking.checkOut,
            guests: booking.guests,
            rooms: booking.rooms,
            amount: booking.amount,
            currency: booking.currency,
            status: booking.status,
            paymentStatus: booking.paymentStatus,
            notes: booking.notes
        });
        setSelectedBooking(booking);
        setIsEditing(true);
        setIsFormOpen(true);
        setActionMenuId(null);
    };

    const handleSaveBooking = async () => {
        if (isEditing && selectedBooking) {
            try {
                // Build the update payload mapped back to MongoDB schema
                const updatePayload: any = {
                    'hotel.name': formData.serviceName,
                    'offer.checkIn': formData.checkIn ? new Date(formData.checkIn).toISOString() : undefined,
                    'offer.checkOut': formData.checkOut ? new Date(formData.checkOut).toISOString() : undefined,
                    'offer.nights': formData.checkIn && formData.checkOut
                        ? Math.round((new Date(formData.checkOut).getTime() - new Date(formData.checkIn).getTime()) / 86400000)
                        : undefined,
                    'contact.email': formData.customerEmail,
                    'contact.phone': formData.customerPhone,
                    'payment.totalPrice': formData.amount,
                    'payment.currency': formData.currency,
                    'payment.paymentStatus': formData.paymentStatus === 'Paid' ? 'Completed' : formData.paymentStatus === 'Unpaid' ? 'Pending' : formData.paymentStatus,
                    status: formData.status,
                    notes: formData.notes
                };
                // Clean undefined values
                Object.keys(updatePayload).forEach(k => updatePayload[k] === undefined && delete updatePayload[k]);

                const res = await fetch(`/api/bookings/${selectedBooking.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(updatePayload)
                });
                const data = await res.json();
                if (data.success && data.booking) {
                    // Update local state with the server response
                    setBookings(prev => prev.map(b => b.id === selectedBooking.id ? mapMongoBooking(data.booking) : b));
                } else {
                    console.error('Failed to save booking:', data.message);
                    alert('Failed to save: ' + (data.message || 'Unknown error'));
                    return;
                }
            } catch (err) {
                console.error('Network error saving booking:', err);
                alert('Network error saving booking');
                return;
            }
        } else {
            const newBooking: Booking = {
                ...formData,
                id: `BK-2025-${String(bookings.length + 1).padStart(3, '0')}`,
                createdAt: new Date().toISOString().split('T')[0]
            };
            setBookings(prev => [newBooking, ...prev]);
        }
        setIsFormOpen(false);
        setFormData(emptyBooking);
    };

    const handleCancelBooking = (id: string) => {
        setBookings(prev => prev.map(b => b.id === id ? { ...b, status: 'Cancelled' } : b));
        setActionMenuId(null);
    };

    const handleDeleteBooking = (id: string) => {
        if (confirm('Are you sure you want to delete this booking?')) {
            setBookings(prev => prev.filter(b => b.id !== id));
        }
        setActionMenuId(null);
    };

    const handleExport = () => {
        const headers = ['ID', 'Customer', 'Email', 'Service', 'Destination', 'Check-in', 'Check-out', 'Guests', 'Amount', 'Status', 'Payment'];
        const csvContent = [
            headers.join(','),
            ...filteredBookings.map(b => [
                b.id, b.customerName, b.customerEmail, b.serviceName, b.destination, b.checkIn, b.checkOut, b.guests, `${b.amount} ${b.currency}`, b.status, b.paymentStatus
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `bookings_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    return (
        <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-gray-50 dark:bg-gray-900 min-h-full">
            <div className="max-w-[1400px] mx-auto space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Reservations</h1>
                        <p className="text-gray-500 dark:text-gray-400">Manage all bookings and travel services</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={handleExport} className="px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 shadow-sm transition-all">
                            <span className="material-icons-round text-gray-500">file_download</span>
                            Export CSV
                        </button>
                        <button onClick={handleOpenCreate} className="px-4 py-2.5 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 flex items-center gap-2 shadow-lg shadow-primary/20 transition-all">
                            <span className="material-icons-round">add</span>
                            New Booking
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Bookings</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.total}</p>
                            </div>
                            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <span className="material-icons-round text-blue-600 dark:text-blue-400">book_online</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Revenue</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">€{stats.totalRevenue.toLocaleString()}</p>
                            </div>
                            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                <span className="material-icons-round text-green-600 dark:text-green-400">euro</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Pending Payments</p>
                                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">€{stats.pendingPayments.toLocaleString()}</p>
                            </div>
                            <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                                <span className="material-icons-round text-orange-600 dark:text-orange-400">pending</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cancellation Rate</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.cancellationRate}%</p>
                            </div>
                            <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                <span className="material-icons-round text-red-600 dark:text-red-400">cancel</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="relative w-full md:w-96">
                        <span className="material-icons-round absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">search</span>
                        <input
                            type="text"
                            placeholder="Search by name, ID, service or destination..."
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-gray-800 dark:text-white"
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                    <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
                        {['All', 'Confirmed', 'Pending', 'Completed', 'Cancelled'].map(status => (
                            <button
                                key={status}
                                onClick={() => { setFilterStatus(status); setCurrentPage(1); }}
                                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${filterStatus === status
                                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-md'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                            >
                                {status}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
                    {/* Desktop Table — hidden on mobile */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                <tr>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Booking ID</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Customer</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Service</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dates</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Payment</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Amount</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {loading ? (
                                    <tr>
                                        <td colSpan={8} className="px-6 py-16 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
                                                <span className="text-gray-500 dark:text-gray-400">Loading bookings...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : error ? (
                                    <tr>
                                        <td colSpan={8} className="px-6 py-16 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <span className="material-icons-round text-4xl text-red-400">error_outline</span>
                                                <span className="text-red-500">{error}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : paginatedBookings.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="px-6 py-16 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <span className="material-icons-round text-4xl text-gray-300 dark:text-gray-600">inbox</span>
                                                <span className="text-gray-500 dark:text-gray-400">No bookings found</span>
                                                <span className="text-xs text-gray-400 dark:text-gray-500">Completed bookings will appear here automatically</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : paginatedBookings.map((booking) => (
                                    <tr key={booking.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group cursor-pointer" onClick={() => handleOpenDetail(booking)}>
                                        <td className="px-4 py-3">
                                            <span className="font-mono text-sm text-primary font-medium">{booking.reservationNumber || booking.id}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col">
                                                <span className="font-medium text-gray-900 dark:text-white text-sm">{booking.customerName}</span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[160px]">{booking.customerEmail}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <span className={`material-icons-round text-lg ${booking.serviceType === 'Flight' ? 'text-blue-500' :
                                                    booking.serviceType === 'Hotel' ? 'text-purple-500' :
                                                        booking.serviceType === 'Tour' ? 'text-orange-500' : 'text-green-500'
                                                    }`}>
                                                    {getServiceIcon(booking.serviceType)}
                                                </span>
                                                <div className="flex flex-col">
                                                    <span className="text-sm text-gray-700 dark:text-gray-200 truncate max-w-[140px]">{booking.serviceName}</span>
                                                    <span className="text-xs text-gray-400">{booking.destination}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                            <div className="flex flex-col">
                                                <span>{booking.checkIn}</span>
                                                {booking.checkIn !== booking.checkOut && <span className="text-xs text-gray-400">to {booking.checkOut}</span>}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(booking.status)}`}>
                                                {booking.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${getPaymentColor(booking.paymentStatus)}`}>
                                                {booking.paymentStatus}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right font-bold text-gray-900 dark:text-white">
                                            €{booking.amount.toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                            <div className="relative inline-block">
                                                <button
                                                    onClick={() => setActionMenuId(actionMenuId === booking.id ? null : booking.id)}
                                                    className="text-gray-400 hover:text-primary transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                                >
                                                    <span className="material-icons-round">more_vert</span>
                                                </button>
                                                {actionMenuId === booking.id && (
                                                    <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50">
                                                        <button onClick={() => handleOpenDetail(booking)} className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                                                            <span className="material-icons-round text-lg">visibility</span> View Details
                                                        </button>
                                                        <button onClick={() => handleOpenEdit(booking)} className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                                                            <span className="material-icons-round text-lg">edit</span> Edit
                                                        </button>
                                                        {booking.status !== 'Cancelled' && booking.status !== 'Completed' && (
                                                            <button onClick={() => handleCancelBooking(booking.id)} className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 flex items-center gap-2">
                                                                <span className="material-icons-round text-lg">block</span> Cancel
                                                            </button>
                                                        )}
                                                        <button onClick={() => handleDeleteBooking(booking.id)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 border-t border-gray-100 dark:border-gray-700">
                                                            <span className="material-icons-round text-lg">delete</span> Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card Layout — shown only on mobile */}
                    <div className="md:hidden">
                        {loading ? (
                            <div className="p-8 text-center">
                                <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                                <span className="text-gray-500 dark:text-gray-400 text-sm">Loading bookings...</span>
                            </div>
                        ) : error ? (
                            <div className="p-8 text-center">
                                <span className="material-icons-round text-3xl text-red-400 block mb-2">error_outline</span>
                                <span className="text-red-500 text-sm">{error}</span>
                            </div>
                        ) : paginatedBookings.length === 0 ? (
                            <div className="p-8 text-center">
                                <span className="material-icons-round text-3xl text-gray-300 dark:text-gray-600 block mb-2">inbox</span>
                                <span className="text-gray-500 dark:text-gray-400 text-sm">No bookings found</span>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                {paginatedBookings.map((booking) => (
                                    <div key={booking.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer active:bg-gray-100 dark:active:bg-gray-700" onClick={() => handleOpenDetail(booking)}>
                                        {/* Top row: ID + Amount */}
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-mono text-xs text-primary font-bold truncate max-w-[55%]">{booking.reservationNumber || booking.id}</span>
                                            <span className="font-bold text-gray-900 dark:text-white text-sm">€{booking.amount.toLocaleString()}</span>
                                        </div>
                                        {/* Customer + Service */}
                                        <div className="flex items-start gap-2 mb-2">
                                            <span className={`material-icons-round text-base mt-0.5 ${booking.serviceType === 'Flight' ? 'text-blue-500' :
                                                booking.serviceType === 'Hotel' ? 'text-purple-500' :
                                                    booking.serviceType === 'Tour' ? 'text-orange-500' : 'text-green-500'
                                                }`}>
                                                {getServiceIcon(booking.serviceType)}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{booking.serviceName}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{booking.customerName} • {booking.destination}</p>
                                            </div>
                                        </div>
                                        {/* Bottom row: Dates + Status badges */}
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {booking.checkIn}{booking.checkIn !== booking.checkOut ? ` → ${booking.checkOut}` : ''}
                                            </span>
                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getStatusColor(booking.status)}`}>
                                                    {booking.status}
                                                </span>
                                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${getPaymentColor(booking.paymentStatus)}`}>
                                                    {booking.paymentStatus}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                            Showing <span className="text-gray-900 dark:text-white font-bold">{((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredBookings.length)}</span> of <span className="text-gray-900 dark:text-white font-bold">{filteredBookings.length}</span> bookings
                        </span>
                        <div className="flex gap-2 flex-wrap justify-center">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${currentPage === page
                                        ? 'bg-primary text-white shadow-md'
                                        : 'border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                        }`}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Detail Modal */}
            {isDetailOpen && selectedBooking && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setIsDetailOpen(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedBooking.id}</h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Created on {selectedBooking.createdAt}</p>
                            </div>
                            <button onClick={() => setIsDetailOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white p-1">
                                <span className="material-icons-round">close</span>
                            </button>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="flex gap-2">
                                <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getStatusColor(selectedBooking.status)}`}>{selectedBooking.status}</span>
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPaymentColor(selectedBooking.paymentStatus)}`}>{selectedBooking.paymentStatus}</span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                <div>
                                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Customer Details</h4>
                                    <div className="space-y-2">
                                        <p className="text-gray-900 dark:text-white font-medium">{selectedBooking.customerName}</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">{selectedBooking.customerEmail}</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">{selectedBooking.customerPhone}</p>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Service Details</h4>
                                    <div className="space-y-2">
                                        <p className="text-gray-900 dark:text-white font-medium flex items-center gap-2">
                                            <span className="material-icons-round text-lg text-primary">{getServiceIcon(selectedBooking.serviceType)}</span>
                                            {selectedBooking.serviceName}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">{selectedBooking.destination}</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">{selectedBooking.guests} guests • {selectedBooking.rooms} rooms</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Check-in</p>
                                        <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedBooking.checkIn}</p>
                                    </div>
                                    <span className="material-icons-round text-gray-300">arrow_forward</span>
                                    <div className="text-right">
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Check-out</p>
                                        <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedBooking.checkOut}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Flight Information */}
                            {selectedBooking.flights && selectedBooking.flights.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">✈ Flight Information</h4>
                                    <div className="space-y-3">
                                        {selectedBooking.flights.filter(f => f.type !== 'Transfer').map((f, idx) => {
                                            const depTime = f.departureDate ? new Date(f.departureDate).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) : '—';
                                            const arrTime = f.arrivalDate ? new Date(f.arrivalDate).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) : '—';
                                            const depDate = f.departureDate ? new Date(f.departureDate).toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
                                            const arrDate = f.arrivalDate ? new Date(f.arrivalDate).toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
                                            let duration = '';
                                            if (f.departureDate && f.arrivalDate) {
                                                const ms = new Date(f.arrivalDate).getTime() - new Date(f.departureDate).getTime();
                                                if (ms > 0) { const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000); duration = `${h}h ${m}m`; }
                                            }
                                            const isOutbound = f.direction === 'Outbound';
                                            return (
                                                <div key={idx} className={`bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border-l-4 ${isOutbound ? 'border-blue-400' : 'border-amber-400'}`}>
                                                    <div className="flex items-center justify-between mb-3">
                                                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${isOutbound ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'}`}>
                                                            {isOutbound ? '🛫 OUTBOUND' : '🛬 RETURN'}
                                                        </span>
                                                        {f.cabinClass && <span className="text-xs text-gray-500 bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded-full">{f.cabinClass}</span>}
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <div className="text-center">
                                                            <div className="text-xl font-bold text-gray-900 dark:text-white">{depTime}</div>
                                                            <div className="text-xs text-gray-500">{depDate}</div>
                                                            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1">{f.departureAirport || '—'}</div>
                                                        </div>
                                                        <div className="flex-1 mx-4 text-center">
                                                            {duration && <div className="text-xs text-gray-400 mb-1">{duration}</div>}
                                                            <div className="border-t-2 border-dashed border-gray-300 dark:border-gray-600 relative">
                                                                <span className="absolute right-0 -top-1.5 text-gray-400">▸</span>
                                                            </div>
                                                            {f.flightNumber && <div className="text-xs text-gray-400 mt-1">{f.carrier} {f.flightNumber}</div>}
                                                        </div>
                                                        <div className="text-center">
                                                            <div className="text-xl font-bold text-gray-900 dark:text-white">{arrTime}</div>
                                                            <div className="text-xs text-gray-500">{arrDate}</div>
                                                            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1">{f.arrivalAirport || '—'}</div>
                                                        </div>
                                                    </div>
                                                    {f.serviceName && <div className="mt-2 text-xs text-gray-500">{f.serviceName}</div>}
                                                </div>
                                            );
                                        })}
                                        {/* Transfers */}
                                        {selectedBooking.flights.filter(f => f.type === 'Transfer').map((t, idx) => (
                                            <div key={`transfer-${idx}`} className="bg-purple-50 dark:bg-purple-900/10 rounded-xl p-4 border-l-4 border-purple-400">
                                                <span className="text-xs font-bold px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">🚐 TRANSFER</span>
                                                <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                                                    <strong>{t.departureAirport || '—'}</strong> → <strong>{t.arrivalAirport || '—'}</strong>
                                                    {t.departureDate && <span className="text-gray-500 ml-2">{new Date(t.departureDate).toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' })} {new Date(t.departureDate).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}</span>}
                                                </div>
                                                {t.serviceName && <div className="text-xs text-gray-400 mt-1">{t.serviceName}</div>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedBooking.notes && (
                                <div>
                                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Notes</h4>
                                    <p className="text-gray-700 dark:text-gray-300 bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg border-l-4 border-yellow-400">{selectedBooking.notes}</p>
                                </div>
                            )}

                            <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
                                <span className="text-sm text-gray-500 dark:text-gray-400">Total Amount</span>
                                <span className="text-2xl font-bold text-gray-900 dark:text-white">{Math.round(selectedBooking.amount).toLocaleString('sv-SE')} {selectedBooking.currency}</span>
                            </div>

                            {/* Transaction IDs */}
                            {(selectedBooking.transactionId || selectedBooking.paymentTransactionId) && (
                                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 space-y-1">
                                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Transaction IDs</h4>
                                    {selectedBooking.transactionId && (
                                        <p className="text-xs text-gray-600 dark:text-gray-300 font-mono break-all">
                                            <span className="font-semibold">Booking TX:</span> {selectedBooking.transactionId}
                                        </p>
                                    )}
                                    {selectedBooking.paymentTransactionId && (
                                        <p className="text-xs text-gray-600 dark:text-gray-300 font-mono break-all">
                                            <span className="font-semibold">Payment TX:</span> {selectedBooking.paymentTransactionId}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Payment Logs Timeline */}
                            {selectedBooking.paymentLogs && selectedBooking.paymentLogs.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">Payment Activity Log</h4>
                                    <div className="space-y-3">
                                        {selectedBooking.paymentLogs.map((log, idx) => {
                                            const actionIcons: Record<string, string> = {
                                                begin_payment: '▶',
                                                transaction_status: '📊',
                                                check_payment: '💳',
                                                finalize_success: '✅',
                                                finalize_error: '❌',
                                                payment_received: '⚠️',
                                                manual_note: '📝'
                                            };
                                            const statusColors: Record<string, string> = {
                                                success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                                                error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                                                pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
                                                info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                            };
                                            const actionLabels: Record<string, string> = {
                                                begin_payment: 'Payment Initiated',
                                                transaction_status: 'Transaction Status Check',
                                                check_payment: 'Payment Verification',
                                                finalize_success: 'Booking Confirmed',
                                                finalize_error: 'Booking Failed',
                                                payment_received: 'Payment Received (Pending)',
                                                manual_note: 'Manual Note'
                                            };
                                            return (
                                                <div key={idx} className="flex gap-3">
                                                    <div className="flex flex-col items-center">
                                                        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm">
                                                            {actionIcons[log.action] || '•'}
                                                        </div>
                                                        {idx < (selectedBooking.paymentLogs?.length || 0) - 1 && (
                                                            <div className="w-px h-full bg-gray-200 dark:bg-gray-700 mt-1"></div>
                                                        )}
                                                    </div>
                                                    <div className="flex-1 pb-3">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                                {actionLabels[log.action] || log.action}
                                                            </span>
                                                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${statusColors[log.status] || 'bg-gray-100 text-gray-600'}`}>
                                                                {log.status}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-gray-400 dark:text-gray-500">
                                                            {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                                                        </p>
                                                        {log.details && Object.keys(log.details).length > 0 && (
                                                            <details className="mt-1">
                                                                <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-primary">Show details</summary>
                                                                <pre className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 rounded p-2 mt-1 overflow-x-auto max-h-32">
                                                                    {JSON.stringify(log.details, null, 2)}
                                                                </pre>
                                                            </details>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                            {emailMessage && (
                                <div className={`mb-3 p-3 rounded-lg text-sm font-medium ${emailMessage.type === 'success' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`}>
                                    {emailMessage.type === 'success' ? '✅' : '❌'} {emailMessage.text}
                                </div>
                            )}
                            <div className="flex gap-3 justify-end flex-wrap items-center">
                                <select value={emailLang} onChange={e => setEmailLang(e.target.value)} className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 cursor-pointer focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
                                    <option value="sv">🇸🇪 Svenska</option>
                                    <option value="en">🇬🇧 English</option>
                                    <option value="da">🇩🇰 Dansk</option>
                                </select>
                                <button onClick={() => sendBookingEmail(selectedBooking.id, 'confirmation')} disabled={emailSending === 'confirmation'} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center gap-2 shadow-sm transition-all disabled:opacity-50">
                                    <span className="material-icons-round text-lg">{emailSending === 'confirmation' ? 'hourglass_top' : 'email'}</span>
                                    {emailSending === 'confirmation' ? 'Sending...' : '📧 Send Confirmation'}
                                </button>
                                <button onClick={() => sendBookingEmail(selectedBooking.id, 'reminder')} disabled={emailSending === 'reminder'} className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium flex items-center gap-2 shadow-sm transition-all disabled:opacity-50">
                                    <span className="material-icons-round text-lg">{emailSending === 'reminder' ? 'hourglass_top' : 'schedule_send'}</span>
                                    {emailSending === 'reminder' ? 'Sending...' : '⏰ Send Reminder'}
                                </button>
                                <button onClick={() => resyncFlights(selectedBooking.id)} disabled={emailSending === 'resync'} className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium flex items-center gap-2 shadow-sm transition-all disabled:opacity-50">
                                    <span className="material-icons-round text-lg">{emailSending === 'resync' ? 'hourglass_top' : 'sync'}</span>
                                    {emailSending === 'resync' ? 'Syncing...' : '✈️ Sync Flights'}
                                </button>
                                <button onClick={() => handleOpenEdit(selectedBooking)} className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2">
                                    <span className="material-icons-round text-lg">edit</span> Edit
                                </button>
                                <button onClick={() => { window.print(); }} className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 flex items-center gap-2">
                                    <span className="material-icons-round text-lg">print</span> Print Voucher
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create/Edit Modal */}
            {isFormOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setIsFormOpen(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{isEditing ? 'Edit Booking' : 'New Booking'}</h2>
                            <button onClick={() => setIsFormOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white p-1">
                                <span className="material-icons-round">close</span>
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Customer Name*</label>
                                    <input type="text" value={formData.customerName} onChange={e => setFormData({ ...formData, customerName: e.target.value })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email*</label>
                                    <input type="email" value={formData.customerEmail} onChange={e => setFormData({ ...formData, customerEmail: e.target.value })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                                    <input type="tel" value={formData.customerPhone} onChange={e => setFormData({ ...formData, customerPhone: e.target.value })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Service Type</label>
                                    <select value={formData.serviceType} onChange={e => setFormData({ ...formData, serviceType: e.target.value as any })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
                                        <option value="Hotel">Hotel</option>
                                        <option value="Flight">Flight</option>
                                        <option value="Tour">Tour</option>
                                        <option value="Transfer">Transfer</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Service Name*</label>
                                    <input type="text" value={formData.serviceName} onChange={e => setFormData({ ...formData, serviceName: e.target.value })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Destination*</label>
                                    <input type="text" value={formData.destination} onChange={e => setFormData({ ...formData, destination: e.target.value })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Check-in*</label>
                                    <input type="date" value={formData.checkIn} onChange={e => setFormData({ ...formData, checkIn: e.target.value })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Check-out*</label>
                                    <input type="date" value={formData.checkOut} onChange={e => setFormData({ ...formData, checkOut: e.target.value })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Guests</label>
                                    <input type="number" min="1" value={formData.guests} onChange={e => setFormData({ ...formData, guests: parseInt(e.target.value) })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rooms</label>
                                    <input type="number" min="0" value={formData.rooms} onChange={e => setFormData({ ...formData, rooms: parseInt(e.target.value) })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount (EUR)*</label>
                                    <input type="number" min="0" value={formData.amount} onChange={e => setFormData({ ...formData, amount: parseFloat(e.target.value) })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                                    <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value as any })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
                                        <option value="Pending">Pending</option>
                                        <option value="Confirmed">Confirmed</option>
                                        <option value="Completed">Completed</option>
                                        <option value="Cancelled">Cancelled</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Status</label>
                                    <select value={formData.paymentStatus} onChange={e => setFormData({ ...formData, paymentStatus: e.target.value as any })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
                                        <option value="Unpaid">Unpaid</option>
                                        <option value="Partial">Partial</option>
                                        <option value="Paid">Paid</option>
                                        <option value="Refunded">Refunded</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                                <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-none"></textarea>
                            </div>
                        </div>
                        <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex gap-3 justify-end">
                            <button onClick={() => setIsFormOpen(false)} className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700">
                                Cancel
                            </button>
                            <button onClick={handleSaveBooking} disabled={!formData.customerName || !formData.customerEmail || !formData.serviceName} className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
                                {isEditing ? 'Save Changes' : 'Create Booking'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Click outside to close action menu */}
            {actionMenuId && <div className="fixed inset-0 z-40" onClick={() => setActionMenuId(null)} />}
        </div>
    );
};

// Wrapper component with tabs for Bookings + Email Settings
const BookingsPage: React.FC = () => {
    const [tab, setTab] = useState<'bookings' | 'email-settings'>('bookings');
    return (
        <div>
            {/* Tabs */}
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl mb-6 w-fit">
                <button
                    onClick={() => setTab('bookings')}
                    className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${tab === 'bookings' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                >
                    <span className="material-icons-round text-lg">receipt_long</span> Bookings
                </button>
                <button
                    onClick={() => setTab('email-settings')}
                    className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${tab === 'email-settings' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                >
                    <span className="material-icons-round text-lg">email</span> Email Settings
                </button>
            </div>
            {tab === 'bookings' ? <BookingsManager /> : <EmailSettingsManager />}
        </div>
    );
};

export default BookingsPage;
